import { useState, useCallback, useEffect, useRef } from "react";
import { fetchSelectedDetails as defaultFetchDetails } from "./api";

// Owns a curated-expert selection: the selected expert objects, their
// publishability decoration, edit-mode hydration from stored IDs, and the
// add/remove/suggest/clear operations (including the suggest merge policy).
//
// Deliberately formik-agnostic so a future RecipientPicker can reuse it with
// its own fetcher. The single seam back to the consuming form is
// `onUserChange`, fired only on user-initiated mutations (not on hydration or
// programmatic reset) so the form can mark its field touched.
//
// Params:
//   initialIds:   stored IDs to hydrate on mount (edit mode). Read once.
//   maxSelection: hard cap applied by add() and suggest().
//   fetchDetails: ID -> full-row fetcher. Defaults to the picker's PostgREST
//                 lookup; injectable for tests / other pickers.
//   onUserChange: called after a user-initiated mutation only.
//
// Returns: { selected, publishabilityById, add, remove, suggest, clear, reset }
//   - clear():  user "Reset" intent — clears selection, fires onUserChange.
//   - reset():  programmatic reset (form-level / post-submit) — clears
//               selection AND publishability, does NOT fire onUserChange
//               (the caller resets its own form/touched state).
export default function useSelectedExperts({
  initialIds = [],
  maxSelection,
  fetchDetails = defaultFetchDetails,
  onUserChange,
}) {
  const [selected, setSelected] = useState([]);
  const [publishabilityById, setPublishabilityById] = useState({});

  const onUserChangeRef = useRef(onUserChange);
  useEffect(() => {
    onUserChangeRef.current = onUserChange;
  });
  const notifyUserChange = useCallback(() => {
    onUserChangeRef.current?.();
  }, []);

  // Edit-mode hydration: fetch full details for stored IDs once on mount.
  // Not a user action — does not notify.
  const initialIdsRef = useRef(initialIds);
  useEffect(() => {
    const ids = initialIdsRef.current;
    if (!ids || ids.length === 0) return undefined;

    let cancelled = false;
    fetchDetails(ids)
      .then((rows) => {
        if (cancelled) return;
        setSelected(
          rows.map((row) => ({
            id: row.id,
            name: row.name,
            description: row.description,
            company: row.company,
            title: row.title,
            migrate_id: row.migrate_id,
            slug: row.slug,
            profile_image_url: row.profile_image_url,
            match_type: "preselected",
          })),
        );
        setPublishabilityById(
          rows.reduce((acc, row) => {
            acc[row.id] = Boolean(row.is_publishable);
            return acc;
          }, {}),
        );
      })
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.error("[useSelectedExperts] failed to hydrate selected experts", err);
      });

    return () => {
      cancelled = true;
    };
  }, [fetchDetails]);

  const add = useCallback(
    (expert) => {
      setSelected((prev) => {
        if (prev.find((e) => e.id === expert.id)) return prev;
        if (prev.length >= maxSelection) return prev;
        return [...prev, expert];
      });
      notifyUserChange();
    },
    [maxSelection, notifyUserChange],
  );

  const remove = useCallback(
    (expertId) => {
      setSelected((prev) => prev.filter((e) => e.id !== expertId));
      notifyUserChange();
    },
    [notifyUserChange],
  );

  // Suggest merge policy lives here (the form owns the selection). Today:
  // replace the whole selection with the suggested set, capped. If we ever
  // want Suggest to append instead, this is the single line to change — the
  // picker contract (emits intent, doesn't decide policy) is unaffected.
  const suggest = useCallback(
    (experts) => {
      setSelected(experts.slice(0, maxSelection));
      notifyUserChange();
    },
    [maxSelection, notifyUserChange],
  );

  const clear = useCallback(() => {
    setSelected([]);
    notifyUserChange();
  }, [notifyUserChange]);

  const reset = useCallback(() => {
    setSelected([]);
    setPublishabilityById({});
  }, []);

  return { selected, publishabilityById, add, remove, suggest, clear, reset };
}
